// 證據匯出用的最小 ZIP（MV.5）：只做「不壓縮」（STORE）——MP4 本來就壓過，再壓沒用；不引新套件。
// 寫：每個檔案是 Blob（不整包複製進記憶體），CRC-32 逐塊算；讀：從中央目錄找檔、只認 STORE。
// 不做 ZIP64 → 整包上限 4 GB（4 台 × 30 分鐘遠低於此）。檔名用 UTF-8（旗標 bit 11）。

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

export function crc32Update(crc: number, buf: Uint8Array): number {
  let c = ~crc >>> 0;
  for (let i = 0; i < buf.length; i++) c = CRC_TABLE[(c ^ buf[i]) & 0xff] ^ (c >>> 8);
  return ~c >>> 0;
}

export async function crc32OfBlob(b: Blob, chunk = 8 << 20): Promise<number> {
  let crc = 0;
  for (let off = 0; off < b.size; off += chunk) crc = crc32Update(crc, new Uint8Array(await b.slice(off, off + chunk).arrayBuffer()));
  return crc;
}

export interface ZipEntry {
  name: string;
  data: Blob;
}

const ZIP_MAX = 0xffffffff;

function dosTime(d: Date): { time: number; date: number } {
  return {
    time: (d.getHours() << 11) | (d.getMinutes() << 5) | Math.floor(d.getSeconds() / 2),
    date: ((Math.max(1980, d.getFullYear()) - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate(),
  };
}

/** 組 ZIP（STORE）。回傳的 Blob 引用各檔案 Blob，不複製內容。 */
export async function buildZip(entries: ZipEntry[], when = new Date()): Promise<Blob> {
  const enc = new TextEncoder();
  const { time, date } = dosTime(when);
  const parts: BlobPart[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;
  for (const e of entries) {
    const name = enc.encode(e.name);
    const size = e.data.size;
    const crc = await crc32OfBlob(e.data);
    if (offset + 30 + name.length + size > ZIP_MAX) throw new Error('zip too large (> 4 GB)');
    const lh = new DataView(new ArrayBuffer(30));
    lh.setUint32(0, 0x04034b50, true);
    lh.setUint16(4, 20, true);
    lh.setUint16(6, 0x0800, true); // UTF-8 檔名
    lh.setUint16(8, 0, true); // STORE
    lh.setUint16(10, time, true);
    lh.setUint16(12, date, true);
    lh.setUint32(14, crc, true);
    lh.setUint32(18, size, true);
    lh.setUint32(22, size, true);
    lh.setUint16(26, name.length, true);
    lh.setUint16(28, 0, true);
    parts.push(lh.buffer, name as BlobPart, e.data);
    const ch = new DataView(new ArrayBuffer(46));
    ch.setUint32(0, 0x02014b50, true);
    ch.setUint16(4, 20, true);
    ch.setUint16(6, 20, true);
    ch.setUint16(8, 0x0800, true);
    ch.setUint16(10, 0, true);
    ch.setUint16(12, time, true);
    ch.setUint16(14, date, true);
    ch.setUint32(16, crc, true);
    ch.setUint32(20, size, true);
    ch.setUint32(24, size, true);
    ch.setUint16(28, name.length, true);
    ch.setUint32(42, offset, true);
    const c = new Uint8Array(46 + name.length);
    c.set(new Uint8Array(ch.buffer), 0);
    c.set(name, 46);
    central.push(c);
    offset += 30 + name.length + size;
  }
  const cdSize = central.reduce((n, c) => n + c.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);
  end.setUint16(8, entries.length, true);
  end.setUint16(10, entries.length, true);
  end.setUint32(12, cdSize, true);
  end.setUint32(16, offset, true);
  return new Blob([...parts, ...(central as BlobPart[]), end.buffer], { type: "application/zip" });
}

export interface ZipFile {
  name: string;
  data: Blob;
  crc32: number;
}

/** 讀 ZIP（只支援 STORE；壓縮過的檔 → 擲錯）。 */
export async function readZip(zip: Blob): Promise<ZipFile[]> {
  const tailLen = Math.min(zip.size, 22 + 0xffff);
  const tail = new DataView(await zip.slice(zip.size - tailLen).arrayBuffer());
  let eocd = -1;
  for (let i = tail.byteLength - 22; i >= 0; i--) {
    if (tail.getUint32(i, true) === 0x06054b50) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new Error('not a zip');
  const count = tail.getUint16(eocd + 10, true);
  const cdSize = tail.getUint32(eocd + 12, true);
  const cdOff = tail.getUint32(eocd + 16, true);
  const cd = new DataView(await zip.slice(cdOff, cdOff + cdSize).arrayBuffer());
  const dec = new TextDecoder();
  const out: ZipFile[] = [];
  let p = 0;
  for (let i = 0; i < count; i++) {
    if (cd.getUint32(p, true) !== 0x02014b50) throw new Error('bad central directory');
    const method = cd.getUint16(p + 10, true);
    const crc = cd.getUint32(p + 16, true);
    const size = cd.getUint32(p + 20, true);
    const nameLen = cd.getUint16(p + 28, true);
    const extraLen = cd.getUint16(p + 30, true);
    const commentLen = cd.getUint16(p + 32, true);
    const lhOff = cd.getUint32(p + 42, true);
    const name = dec.decode(new Uint8Array(cd.buffer, cd.byteOffset + p + 46, nameLen));
    if (method !== 0) throw new Error(`compressed entry not supported: ${name}`);
    const lh = new DataView(await zip.slice(lhOff, lhOff + 30).arrayBuffer());
    if (lh.getUint32(0, true) !== 0x04034b50) throw new Error('bad local header');
    const dataOff = lhOff + 30 + lh.getUint16(26, true) + lh.getUint16(28, true);
    out.push({ name, data: zip.slice(dataOff, dataOff + size), crc32: crc });
    p += 46 + nameLen + extraLen + commentLen;
  }
  return out;
}
